import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import {
  isValidRecoveryDeleteEmail,
  normalizeRecoveryDeleteEmail,
  RECOVERY_DELETE_ACCESS_COLLECTION,
} from "../../../hook/recoveryDeleteAccess";
import PageTitle from "../../layouts/PageTitle";
import Toast, {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../toast/SetToast";

const RecoveryDeleteAccessConfig = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const currentRoles =
    currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];
  const isSuperAdmin = currentRoles.includes("super_admin");

  const loadAccessEmails = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(
        collection(db, RECOVERY_DELETE_ACCESS_COLLECTION),
      );
      const configuredEmails = snapshot.docs
        .map((accessDoc) =>
          normalizeRecoveryDeleteEmail(
            (accessDoc.data() || {}).email || accessDoc.id,
          ),
        )
        .filter(isValidRecoveryDeleteEmail);

      setEmails([...new Set(configuredEmails)].sort());
    } catch (error) {
      console.error("Unable to load Recovery Delete Access emails", error);
      notifyError(
        `Unable to load Recovery Delete Access: ${
          (error && error.message) || error
        }`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) loadAccessEmails();
  }, [isSuperAdmin, loadAccessEmails]);

  if (!isSuperAdmin) return <Redirect to="/" />;

  const addEmail = async () => {
    const email = normalizeRecoveryDeleteEmail(emailInput);
    if (!isValidRecoveryDeleteEmail(email)) {
      notifyWarning("Enter a valid account email.");
      return;
    }
    if (emails.includes(email)) {
      notifyWarning("This account already has Recovery Delete Access.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, RECOVERY_DELETE_ACCESS_COLLECTION, email), {
        email,
      });
      setEmails((current) => [...current, email].sort());
      setEmailInput("");
      notifyTopRight("Recovery Delete Access added");
    } catch (error) {
      console.error("Unable to add Recovery Delete Access", error);
      notifyError(
        `Unable to add Recovery Delete Access: ${
          (error && error.message) || error
        }`,
      );
    } finally {
      setSaving(false);
    }
  };

  const removeEmail = async () => {
    if (!pendingRemoval) return;

    setSaving(true);
    try {
      await deleteDoc(
        doc(db, RECOVERY_DELETE_ACCESS_COLLECTION, pendingRemoval),
      );
      setEmails((current) =>
        current.filter((email) => email !== pendingRemoval),
      );
      setPendingRemoval(null);
      notifyTopRight("Recovery Delete Access removed");
    } catch (error) {
      console.error("Unable to remove Recovery Delete Access", error);
      notifyError(
        `Unable to remove Recovery Delete Access: ${
          (error && error.message) || error
        }`,
      );
    } finally {
      setSaving(false);
    }
  };

  const submitEmail = (event) => {
    event.preventDefault();
    if (!saving && !loading) addEmail();
  };

  return (
    <Fragment>
      <Toast />
      <PageTitle activeMenu="Recovery Delete Access" motherMenu="Admin" />

      <Modal
        centered
        show={Boolean(pendingRemoval)}
        onHide={() => {
          if (!saving) setPendingRemoval(null);
        }}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Remove Recovery Delete Access?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Remove access for <strong>{pendingRemoval}</strong>? This account
            will no longer be able to permanently delete recovery records.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setPendingRemoval(null)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={removeEmail} disabled={saving}>
            {saving ? "Removing…" : "Remove access"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">
            Recovery permanent-delete emails ({emails.length})
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Accounts listed here can use <strong>Delete forever</strong> in
            Booking Recovery and CPAP Trial Recovery. The account must also
            have an <strong>admin</strong> or <strong>super_admin</strong> role
            to open those pages. <strong>admin888</strong> keeps owner access
            and does not need to be listed.
          </p>

          <form
            className="d-flex flex-wrap align-items-end mb-3"
            onSubmit={submitEmail}
          >
            <div className="mr-2 mb-2" style={{ minWidth: 280 }}>
              <label className="mb-1" htmlFor="recovery-delete-access-email">
                Account email
              </label>
              <input
                id="recovery-delete-access-email"
                type="email"
                className="form-control"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                placeholder="name@example.com"
                disabled={saving || loading}
              />
            </div>
            <Button
              className="mb-2"
              type="submit"
              disabled={saving || loading}
            >
              {saving ? "Saving…" : "Add access"}
            </Button>
          </form>

          {loading ? (
            <div className="text-muted">Loading configured emails…</div>
          ) : emails.length ? (
            <div className="d-flex flex-wrap">
              {emails.map((email) => (
                <span className="badge badge-danger mr-2 mb-2 p-2" key={email}>
                  {email}
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-white p-0 ml-2"
                    onClick={() => setPendingRemoval(email)}
                    disabled={saving}
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-muted">
              No Recovery Delete Access emails are configured.
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default RecoveryDeleteAccessConfig;
